import { useState, useCallback, useMemo } from "react";
import { useAccount } from "../../../context/AccountContext";
import { useRechargeApi } from "./useRechargeApi";
import { checkSubIssues, addDays, isTreatByInfo } from "../helpers";
import type {
  CustomerData,
  SubscriptionData,
  AddressData,
  PaymentMethod,
  AddressGroup,
  DogProfile,
  AllSubsIssues,
} from "../types";

export function useDevCustomer() {
  const { products } = useAccount();
  const { call } = useRechargeApi();

  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>([]);
  const [addresses, setAddresses] = useState<AddressData[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [upcomingCharges, setUpcomingCharges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isTreatProduct = useCallback((productOrInfo: any): boolean => {
    const tags = String(productOrInfo?.tags || "").toLowerCase();
    const title = String(productOrInfo?.title || productOrInfo?.productTitle || "").toLowerCase();
    const productType = String(productOrInfo?.product_type || "").toLowerCase();

    if (tags.includes("treat")) return true;
    if (productType === "treat" || productType === "treats") return true;

    return (
      title.includes("stick") ||
      title.includes("strip") ||
      title.includes("pizzle") ||
      title.includes("biscuit") ||
      title.includes("snack") ||
      title.includes("chew") ||
      title.includes("dental")
    );
  }, []);

  const isBundleSub = useCallback((sub: SubscriptionData): boolean => {
    const props = sub.properties || [];
    return props.some(p => p.name === "_paws_meals_json" || p.name === "_paws_box_variant_id");
  }, []);

  const getVariantInfo = useCallback((variantId: string) => {
    for (const product of products) {
      const variant = product.variants?.find((v: any) => String(v.id) === variantId);
      if (variant) {
        return {
          productTitle: product.title,
          variantTitle: variant.title,
          image: product.images?.[0]?.src || product.image?.src,
          tags: product.tags,
        };
      }
    }
    return null;
  }, [products]);

  const fetchAllData = useCallback(async (customerId: string) => {
    const [subsRes, addrRes, pmRes, upRes] = await Promise.all([
      call<any>("/recharge/subscriptions", { params: { customer_id: customerId } }),
      call<any>("/recharge/addresses", { params: { customer_id: customerId } }),
      call<any>("/recharge/payment-methods", { params: { customer_id: customerId } }),
      call<any>("/recharge/upcoming", { params: { customer_id: customerId } }),
    ]);

    if (!subsRes.ok) throw new Error(subsRes.error || "Failed to fetch subscriptions");
    setSubscriptions(subsRes.data?.subscriptions || []);
    setAddresses(addrRes.ok ? addrRes.data?.addresses || [] : []);
    setPaymentMethods(pmRes.ok ? pmRes.data?.payment_methods || [] : []);
    setUpcomingCharges(upRes.ok ? upRes.data?.charges || [] : []);
  }, [call]);

  const searchByEmail = useCallback(async (email: string) => {
    if (!email.trim()) return;

    setLoading(true);
    setError(null);
    setCustomer(null);
    setSubscriptions([]);
    setAddresses([]);
    setPaymentMethods([]);
    setUpcomingCharges([]);

    try {
      const res = await call<any>("/recharge/customer/by-email", { params: { email: email.trim() } });

      if (!res.ok) throw new Error(res.error || "Customer not found");

      const customers = res.data?.customers || [];
      if (customers.length === 0) throw new Error("No customer found with that email");

      const found = customers[0];
      setCustomer({
        id: String(found.id),
        email: found.email,
        first_name: found.first_name,
        last_name: found.last_name,
      });

      await fetchAllData(String(found.id));
    } catch (err: any) {
      setError(err.message || "Failed to search customer");
    } finally {
      setLoading(false);
    }
  }, [call, fetchAllData]);

  const searchById = useCallback(async (id: string) => {
    if (!id.trim()) return;

    setLoading(true);
    setError(null);
    setCustomer(null);
    setSubscriptions([]);
    setAddresses([]);
    setPaymentMethods([]);
    setUpcomingCharges([]);

    try {
      await fetchAllData(id.trim());
      setCustomer({ id: id.trim(), email: "Unknown (searched by ID)" });
    } catch (err: any) {
      setError(err.message || "Failed to fetch customer data");
    } finally {
      setLoading(false);
    }
  }, [fetchAllData]);

  const refreshData = useCallback(async () => {
    if (!customer) return;
    try {
      await fetchAllData(customer.id);
    } catch (err: any) {
      setError(err.message || "Failed to refresh data");
    }
  }, [customer, fetchAllData]);

  // Computed: address groups
  const addressGroups = useMemo((): AddressGroup[] => {
    const groups: Record<string, AddressGroup> = {};

    for (const addr of addresses) {
      const addressId = String(addr.id || "");
      if (!addressId) continue;

      let deliveryDate: string | null = null;
      const orderAttrs = addr.order_attributes || [];
      const noteAttrs = addr.note_attributes || [];

      const orderDeliveryAttr = orderAttrs.find(a => a.name === "__qivora_delivery_date");
      if (orderDeliveryAttr?.value) {
        deliveryDate = orderDeliveryAttr.value;
      } else {
        const noteDeliveryAttr = noteAttrs.find(a => a.name === "__qivora_delivery_date");
        if (noteDeliveryAttr?.value) {
          deliveryDate = noteDeliveryAttr.value;
        }
      }

      groups[addressId] = {
        addressId,
        address: addr,
        deliveryDate,
        shipDate: deliveryDate ? addDays(deliveryDate, -1) : null,
        chargeDate: deliveryDate ? addDays(deliveryDate, -3) : null,
        bundleSubs: [],
        treatSubs: [],
        allSubs: [],
      };
    }

    for (const sub of subscriptions) {
      const addressId = String(sub.address_id || "unknown");

      if (!groups[addressId]) {
        groups[addressId] = {
          addressId,
          address: null,
          deliveryDate: null,
          shipDate: null,
          chargeDate: null,
          bundleSubs: [],
          treatSubs: [],
          allSubs: [],
        };
      }

      groups[addressId].allSubs.push(sub);

      if (isBundleSub(sub)) {
        groups[addressId].bundleSubs.push(sub);
      } else {
        const variantId = String(sub.shopify_variant_id || sub.external_variant_id || "");
        const info = getVariantInfo(variantId);
        if (info && isTreatProduct(info)) {
          groups[addressId].treatSubs.push(sub);
        } else {
          groups[addressId].bundleSubs.push(sub);
        }
      }
    }

    for (const group of Object.values(groups)) {
      if (!group.deliveryDate && group.allSubs.length > 0) {
        const firstSub = group.allSubs[0];
        if (firstSub.next_charge_scheduled_at) {
          group.chargeDate = firstSub.next_charge_scheduled_at.split("T")[0];
          group.deliveryDate = addDays(group.chargeDate, 3);
          group.shipDate = addDays(group.chargeDate, 2);
        }
      }
    }

    return Object.values(groups).filter(g => g.allSubs.length > 0);
  }, [addresses, subscriptions, isBundleSub, isTreatProduct, getVariantInfo]);

  // Computed: dog profiles
  const dogProfiles = useMemo((): DogProfile[] => {
    const dogsMap = new Map<string, DogProfile>();

    for (const sub of subscriptions) {
      const dogJsonStr = sub.properties?.find(p => p.name === "_paws_dog_json")?.value;
      if (dogJsonStr) {
        try {
          const dogData = JSON.parse(dogJsonStr);
          if (dogData && dogData.name) {
            const key = dogData.id || dogData.name;
            if (!dogsMap.has(key)) {
              dogsMap.set(key, {
                id: key,
                name: dogData.name,
                breed: dogData.breed,
                weightKg: dogData.weightKg,
                dailyGrams: dogData.dailyGrams,
                selectedPackSize: dogData.selectedPackSize,
                selectedPlanDays: dogData.selectedPlanDays,
              });
            }
          }
        } catch { }
      }
    }

    return Array.from(dogsMap.values());
  }, [subscriptions]);

  // Computed: all subs issues
  const allSubsIssues = useMemo((): AllSubsIssues => {
    const issuesMap = new Map<string, any>();
    let totalIssueCount = 0;
    const subsWithIssues: Array<{ sub: SubscriptionData; issues: any }> = [];

    for (const sub of subscriptions) {
      const issues = checkSubIssues(sub, products);
      issuesMap.set(sub.id, issues);
      if (issues.hasIssues) {
        totalIssueCount += issues.issueCount;
        subsWithIssues.push({ sub, issues });
      }
    }

    return { issuesMap, totalIssueCount, subsWithIssues, hasAnyIssues: subsWithIssues.length > 0 };
  }, [subscriptions, products]);

  // Computed: recipe products for meal editing
  const recipeProducts = useMemo(() => {
    return products.filter((p: any) => {
      const type = String(p.product_type || "").toLowerCase();
      const tags = String(p.tags || "").toLowerCase();
      const handle = String(p.handle || "").toLowerCase();
      if (handle.includes("hidden")) return false;
      return type === "recipe" || tags.includes("recipe") || tags.includes("meal");
    });
  }, [products]);

  return {
    customer,
    subscriptions,
    addresses,
    paymentMethods,
    upcomingCharges,
    loading,
    error,
    setError,
    searchByEmail,
    searchById,
    refreshData,
    addressGroups,
    dogProfiles,
    allSubsIssues,
    recipeProducts,
    getVariantInfo,
    isBundleSub,
    isTreatProduct,
  };
}
